import { FaMoneyBill } from "react-icons/fa";
import { defineType } from "sanity";
import allPageBuilderSections, {
  pageBuilderOptions
} from "../../common/allPageBuilderSections";
import { filterableMultilingualBlockContent } from "../base";
import filterableNumber from "../base/filterableNumber";
import { multilingualStringPreview } from "../base/multilingualArrayString";
import { multilingualOrderingField } from "../../common/ordering";
import { imageWithAlt } from "../objects";
import {
  asCardBlockContent,
  asCardWithIcon,
  asPromoFeaturedCard,
  metadataWithoutParent
} from "../page-fragments";

export default defineType({
  name: "exclusiveOfferPerPromo",
  title: "Exclusive Offer Per Promo",
  type: "document",
  icon: FaMoneyBill,
  preview: {
    select: {
      title: "title",
      media: "heroImage"
    },
    prepare({ title, media }) {
      return {
        title: multilingualStringPreview(title),
        media
      };
    }
  },
  orderings: [
    {
      title: "Title",
      name: "titleAsc",
      by: [{ field: multilingualOrderingField("title"), direction: "asc" }]
    }
  ],
  fieldsets: [
    {
      name: "urgencyBox",
      title: "Urgency Box",
      options: { collapsible: true, collapsed: true }
    },
    {
      name: "countdown",
      title: "Countdown",
      options: { collapsible: true, collapsed: true }
    },
    {
      name: "hero",
      title: "Hero",
      options: { collapsible: true, collapsed: true }
    },
    {
      name: "content",
      title: "Content",
      options: { collapsible: true, collapsed: true }
    },
    {
      name: "features",
      title: "Features",
      options: { collapsible: true, collapsed: true }
    },
    {
      name: "visibility",
      title: "Visibility",
      options: { collapsible: true, collapsed: false }
    },
    {
      name: "cards",
      title: "Cards",
      options: { collapsible: false }
    },
    {
      name: "targetedOffer",
      title: "Targeted Offer Content",
      options: { collapsible: true, collapsed: true }
    },
    {
      name: "icons",
      title: "Icons",
      options: { collapsible: true, collapsed: true }
    },
    {
      name: "stickybar",
      title: "Sticky Bar",
      options: { collapsible: true, collapsed: true }
    }
  ],
  groups: [
    {
      name: "visibility",
      title: "Visibility"
    },
    { name: "cards", title: "Cards" },
    { name: "countdown", title: "Countdown" },
    { name: "urgencyBox", title: "Urgency Box" },
    {
      name: "seo",
      title: "SEO, Routing & Tracking"
    },
    {
      name: "targetedOffer",
      title: "Targeted Offer Content"
    }
  ],
  fields: [
    {
      name: "configuration",
      title: "Promo Configuration",
      type: "reference",
      to: [{ type: "promoConfiguration" }],
      validation: (Rule) => Rule.required()
    },
    {
      name: "promoType",
      title: "Promo Type",
      type: "string",
      initialValue: "standard",
      options: {
        list: [
          {
            title: "Standard",
            value: "standard"
          },
          {
            title: "Targeted",
            value: "targeted"
          },
          { title: "Benefit", value: "benefit" },
          { title: "Loyalty Benefit", value: "loyaltyBenefit" }
        ]
      },
      description: "If not specified, Standard Offer is used by default"
    },
    {
      name: "hideInHomepage",
      title: "Hide in Homepage",
      type: "boolean",
      fieldset: "visibility",
      group: "visibility"
    },
    {
      name: "hideInCollections",
      title: "Hide in Collections (page builder offers mosaic/slider)",
      type: "boolean",
      fieldset: "visibility",
      group: "visibility"
    },
    {
      name: "showInCruiseFareTab",
      title: "Show a badge in the cruise fare tab",
      type: "boolean",
      fieldset: "visibility",
      group: "visibility"
    },
    {
      name: "showInCruiseCard",
      title: "Show a badge in the cruise card",
      type: "boolean",
      fieldset: "visibility",
      group: "visibility"
    },
    {
      name: "showInCruisePageHero",
      title: "Show a badge in the cruise page hero",
      type: "boolean",
      fieldset: "visibility",
      group: "visibility"
    },
    {
      name: "showPageHeroBanner",
      title: "Show a banner in the homepage hero",
      type: "boolean",
      fieldset: "visibility",
      group: "visibility"
    },
    {
      name: "showDestinationCruiseSlider",
      title: "Show a last minute cruise slider in destination and ship page",
      type: "boolean",
      fieldset: "visibility",
      group: "visibility"
    },
    {
      name: "hideInMainMenu",
      title: "Hide the promo in the main menu",
      type: "boolean",
      fieldset: "visibility",
      group: "visibility"
    },
    {
      name: "showInVSMenu",
      title: "Show in VS section of the main menu",
      type: "boolean",
      fieldset: "visibility",
      group: "visibility"
    },
    {
      name: "hideInCruisePageOfferSlider",
      title: "Hide the promo in the cruise page offers slider",
      type: "boolean",
      fieldset: "visibility",
      group: "visibility"
    },
    {
      name: "hideInCruisePageSuiteClassOffers",
      title: "Hide the promo in the suite class offers of cruise and special voyage pages",
      type: "boolean",
      fieldset: "visibility",
      group: "visibility"
    },
    {
      name: "hideInFYCOfferFilter",
      title: "Hide the promo in the offers filter of Algolia",
      type: "boolean",
      fieldset: "visibility",
      group: "visibility"
    },
    {
      name: "hideInTAndCPage",
      title: "Hide the offer in the T&C offer page ",
      type: "boolean",
      fieldset: "visibility",
      group: "visibility"
    },
    {
      name: "hideCardCta",
      title: "Hide the discover more cta in promo card",
      type: "boolean",
      fieldset: "visibility",
      group: "visibility"
    },
    {
      name: "order",
      title: "Order",
      description:
        "in ascending order (1: first in the list, 100: last in the list)",
      type: "array",
      of: [
        {
          type: filterableNumber.name
        }
      ],
      fieldset: "visibility",
      group: "visibility"
    },
    {
      name: "heroImage",
      title: "Hero Image",
      type: "imageWithAlt",
      options: { hotspot: true },
      validation: (Rule) => Rule.required(),
      fieldset: "hero"
    },
    {
      name: "title",
      title: "Title",
      type: "multilingualArrayString",
      validation: (Rule) => Rule.required(),
      fieldset: "hero"
    },
    {
      type: "ctaButtons",
      name: "ctaButtons",
      title: "CTA Buttons",
      fieldset: "hero"
    },

    {
      name: "extendedTitle",
      title: "Extended Title",
      description:
        "If present, will be shown in the offer page as title. You can use tokens.",
      type: "multilingualArrayBlockContent",
      fieldset: "content"
    },
    {
      name: "description",
      title: "Description",
      type: "multilingualArrayBlockContent",
      fieldset: "content",
      validation: (Rule) => Rule.required()
    },
    {
      name: "customCTALabel",
      title: "Custom CTA Label",
      description:
        "if empty, the default CTA label (Request a quote for an exclusive offer) will be used",
      type: "multilingualArrayString",
      fieldset: "content",
      options: {
        collapsible: true,
        collapsed: true
      }
    },
    {
      name: "secondaryCTA",
      title: "Secondary CTA",
      type: "pageCTA",
      fieldset: "content",
      options: {
        collapsible: true,
        collapsed: true
      }
    },
    {
      name: "tertiaryCTA",
      title: "Tertiary CTA",
      type: "pageCTA",
      fieldset: "content",
      options: {
        collapsible: true,
        collapsed: true
      }
    },
    {
      name: "featureTitle",
      title: "Title",
      type: "multilingualArrayString",
      fieldset: "features"
    },
    {
      name: "features",
      type: "array",
      of: [{ type: "exclusiveOfferFeature" }],
      fieldset: "features"
    },
    {
      name: "sections",
      type: "array",
      title: "Page sections",
      of: allPageBuilderSections(),
      options: {
        ...pageBuilderOptions
      }
    },
    {
      fieldset: "icons",
      name: "iconVertical",
      title: "Icon vertical (SVG)",
      description: "usually black and white",
      type: "image",
      options: {
        accept: "image/svg+xml"
      }
    },
    {
      fieldset: "icons",
      name: "iconHorizontal",
      title: "Icon horizontal (SVG)",
      description: "usually green color",
      type: "image",
      options: {
        accept: "image/svg+xml"
      }
    },
    {
      fieldset: "icons",
      name: "iconSmall",
      title: "Icon small (SVG)",
      description: "usually black and white and square",
      type: "image",
      options: {
        accept: "image/svg+xml"
      }
    },
    {
      name: "fycFilterDescription",
      title: "FYC filter description and Trending now description",
      description:
        "This is used in the FYC offer filter and as Trending Now content",
      type: "multilingualArrayText",
      options: { collapsible: true, collapsed: true },
      group: "cards"
    },
    {
      fieldset: "cards",
      name: "cardWithIcon",
      title:
        "Render as Slider Card with Icon. Used in the cruise page offer card",
      type: asCardWithIcon.name,
      group: "cards"
    },
    {
      fieldset: "cards",
      name: "card",
      title: "Render as Picture Card. Used in sliders and mosaics",
      type: asCardBlockContent.name,
      group: "cards"
    },
    {
      fieldset: "cards",
      name: "farePageCard",
      title: "Render as Fare Page Card. Used in the single fare page",
      type: asPromoFeaturedCard.name,
      group: "cards"
    },
    {
      title: "Promo Modal Background",
      name: "promoModalBackground",
      type: imageWithAlt.name,
      group: "targetedOffer",
      fieldset: "targetedOffer"
    },
    {
      title: "Promo Banner",
      name: "promoBannerContent",
      type: "multilingualArrayBlockContent",
      group: "targetedOffer",
      fieldset: "targetedOffer"
    },
    {
      title: "Promo Modal",
      name: "promoModalContent",
      type: "blockContentTitleAndContent",
      group: "targetedOffer",
      fieldset: "targetedOffer"
    },
    {
      title: "Promo Modal CTA",
      name: "promoModalCTA",
      type: "ctaButtons",
      group: "targetedOffer",
      fieldset: "targetedOffer",
      validation: (Rule) => Rule.max(1),
    },
    {
      name: "showCountdown",
      title: "Show countdown",
      description: "Show countdown in offer components",
      type: "boolean",
      fieldset: "countdown",
      group: "countdown"
    },
    {
      name: "countdownLabel",
      title: "Countdown label",
      type: "multilingualArrayString",
      description: "Label for the countdown timer",
      fieldset: "countdown",
      hidden: ({ parent }) => !parent?.showCountdown,
      group: "countdown"
    },
    {
      name: "showUrgencyBox",
      title: "Show urgency box",
      description: "Show urgency box",
      type: "boolean",
      fieldset: "urgencyBox",
      group: "urgencyBox"
    },
    {
      name: "urgencyBoxContent",
      title: "Urgency Box content",
      type: "multilingualArrayString",
      hidden: ({ parent }) => !parent?.showUrgencyBox,
      fieldset: "urgencyBox",
      group: "urgencyBox"
    },
    {
      name: "termsAndConditionsFootnote",
      title: "T&C Footnote",
      type: "array",
      of: [
        {
          type: filterableMultilingualBlockContent.name
        }
      ]
    },
    {
      name: "termsAndConditions",
      title: "Terms & Conditions",
      type: "array",
      of: [
        {
          type: filterableMultilingualBlockContent.name
        }
      ]
    },
    {
      name: "stickyEnabled",
      title: "Enable",
      type: "boolean",
      fieldset: "stickybar"
    },
    {
      name: "stickyTitle",
      title: "Title",
      type: "multilingualArrayString",
      fieldset: "stickybar",
      hidden: ({ parent }) => !parent?.stickyEnabled
    },
    {
      name: "stickyContent",
      title: "Title",
      type: "multilingualArrayText",
      fieldset: "stickybar",
      hidden: ({ parent }) => !parent?.stickyEnabled
    },
    {
      name: "stickyCTA",
      title: "CTA",
      type: "ctaButtons",
      validation: (Rule) => Rule.max(1),
      fieldset: "stickybar",
      hidden: ({ parent }) => !parent?.stickyEnabled
    },
    {
      name: "metadata",
      title: "SEO, Routing & Tracking",
      type: metadataWithoutParent.name,
      group: "seo"
    }
  ]
});
